import { useState } from 'react';
import Header from '@/components/base/Header';
import Footer from '@/components/base/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';

export default function AboutPage() {
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [formStatus, setFormStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [formError, setFormError] = useState('');

  const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9oojfqftmtnfcfh0b50';

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      setFormStatus({ type: 'success', message: '感谢您的留言，我们会尽快回复！' });
      setContactForm({ name: '', email: '', message: '' });
      return;
    }

    if (contactForm.message.length > 500) {
      setFormError('留言内容不能超过500个字符');
      return;
    }

    try {
      const formData = new FormData(e.currentTarget);
      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // not json
      }

      const serverMsg =
        (parsed as Record<string, unknown> | null)?.meta?.message ||
        (parsed as Record<string, unknown> | null)?.message ||
        (parsed as Record<string, unknown> | null)?.meta?.detail ||
        responseText;

      if (response.ok && parsed && (parsed as Record<string, unknown>).code === 'OK') {
        setFormStatus({ type: 'success', message: '感谢您的留言，我们会尽快回复！' });
        setContactForm({ name: '', email: '', message: '' });
      } else {
        setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后再试');
      }
    } catch {
      setFormError('网络错误，请稍后再试');
    }
  };

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于51动漫网',
    description: '51动漫网是一个专业的免费动漫在线阅读平台，汇集了日漫、国漫、韩漫等各类热门动漫资源。',
    url: 'https://www.51dmw.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '51动漫网',
      url: 'https://www.51dmw.com/',
    },
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '51动漫网',
    url: 'https://www.51dmw.com/',
    description: '51动漫网官方品牌主页，汇集日漫、国漫、韩漫等好看漫画的免费在线阅读平台',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@51dmw.com',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  const testimonials = [
    {
      name: '动漫爱好者小李',
      content: '51动漫网真的是我看过最好的免费动漫平台！资源丰富，更新速度快，画质清晰，而且完全免费，强烈推荐给所有动漫迷！',
      rating: 5,
    },
    {
      name: '追番达人小王',
      content: '用了51动漫网快两年了，日漫国漫韩漫应有尽有，分类清晰，搜索方便。最喜欢的还是下拉式阅读，追番体验太棒了！',
      rating: 5,
    },
    {
      name: '资深漫友老张',
      content: '对比过很多动漫网站，51动漫网的体验是最好的。界面简洁美观，没有烦人的弹窗广告，阅读流程非常顺畅。',
      rating: 5,
    },
    {
      name: '二次元少女小陈',
      content: '51动漫网是我每天必刷的网站！更新超级及时，热门新番一出就能看到。分类功能也特别好用，发现了不少冷门好番。',
      rating: 5,
    },
    {
      name: '上班族漫友小刘',
      content: '通勤路上就用51动漫网看漫画，手机端适配做得很好，下拉式阅读体验流畅。已经把51动漫网推荐给了身边所有喜欢动漫的朋友。',
      rating: 5,
    },
    {
      name: '学生党小赵',
      content: '作为学生党，免费真的很重要！51动漫网不仅免费，内容质量和更新速度也一点不打折扣。感谢51动漫网陪伴我的课余时光。',
      rating: 5,
    },
  ];

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
      <ScrollToTop />
      <Header />
      <Breadcrumb />
      <main className="max-w-[1280px] mx-auto px-4 md:px-6 pb-16">
        <section className="pt-4 mb-12">
          <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 mb-4">
            关于51动漫网
          </h1>
          <p className="text-sm text-foreground-500 max-w-3xl">
            51动漫网官方品牌主页 · 您的随身动漫追更平台
          </p>
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-14">
          <div>
            <h2 className="font-heading text-2xl text-foreground-950 mb-4">品牌介绍</h2>
            <div className="article-content">
              <p>
                <strong>51动漫网</strong>成立于2020年，是国内领先的免费动漫在线阅读平台。作为<strong>51动漫网站</strong>和<strong>51动漫官网</strong>的统一品牌主页，我们致力于为广大动漫爱好者提供最优质的动漫阅读体验。
              </p>
              <p>
                平台汇集了<strong>日漫</strong>、<strong>国漫</strong>、<strong>韩漫</strong>等各类热门动漫作品，涵盖少年热血、玄幻奇幻、恋爱校园、搞笑日常、悬疑推理、科幻未来、战斗冒险等多种类型。无论是经典老番还是最新连载，在51动漫网都能找到。
              </p>
              <p>
                我们采用先进的下拉式阅读技术，为用户提供流畅舒适的阅读体验。同时支持电脑、平板、手机多端访问，让您随时随地畅享动漫乐趣。51动漫网所有内容均免费开放，无需注册登录，点击即可阅读。
              </p>
              <h3>我们的使命</h3>
              <p>
                让每一位动漫爱好者都能方便、快捷地找到并阅读自己喜欢的动漫作品，打造中文互联网上最好的免费动漫阅读社区。
              </p>
              <h3>我们的优势</h3>
              <ul>
                <li><strong>资源丰富</strong>：日漫、国漫、韩漫一网打尽，持续扩充动漫库</li>
                <li><strong>更新及时</strong>：热门连载作品保持同步更新，追番不等待</li>
                <li><strong>完全免费</strong>：所有动漫资源免费阅读，无需任何费用</li>
                <li><strong>体验优秀</strong>：下拉式阅读，高清画质，流畅不卡顿</li>
                <li><strong>多端适配</strong>：电脑、平板、手机均可完美浏览</li>
              </ul>
            </div>
          </div>

          <div className="bg-background-100/50 rounded-2xl p-6 md:p-8">
            <h2 className="font-heading text-2xl text-foreground-950 mb-4">联系方式</h2>
            <div className="space-y-4 mb-8">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center shrink-0">
                  <i className="ri-mail-line text-accent-600"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-800">电子邮箱</p>
                  <p className="text-sm text-foreground-500">contact@51dmw.com</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center shrink-0">
                  <i className="ri-customer-service-line text-accent-600"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-800">在线客服</p>
                  <p className="text-sm text-foreground-500">7×24小时在线服务</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center shrink-0">
                  <i className="ri-map-pin-line text-accent-600"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-800">公司地址</p>
                  <p className="text-sm text-foreground-500">中国·北京市朝阳区</p>
                </div>
              </div>
            </div>

            <h3 className="font-heading text-lg text-foreground-950 mb-4">给我们留言</h3>
            {formStatus ? (
              <div className={`p-4 rounded-xl text-sm ${
                formStatus.type === 'success' ? 'bg-accent-100 text-accent-800' : 'bg-primary-100 text-primary-800'
              }`}>
                <p>{formStatus.message}</p>
                {formStatus.type === 'success' && (
                  <button
                    onClick={() => setFormStatus(null)}
                    className="mt-2 text-accent-600 underline text-xs cursor-pointer"
                  >
                    再发一条
                  </button>
                )}
              </div>
            ) : (
              <form onSubmit={handleContactSubmit} data-readdy-form className="space-y-4">
                <input
                  type="text"
                  name="website_alt"
                  autoComplete="off"
                  tabIndex={-1}
                  aria-hidden="true"
                  readOnly
                />
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    您的姓名
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    value={contactForm.name}
                    onChange={(e) => setContactForm((prev) => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-xl text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    电子邮箱
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    value={contactForm.email}
                    onChange={(e) => setContactForm((prev) => ({ ...prev, email: e.target.value }))}
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-xl text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                    placeholder="请输入您的邮箱"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容 <span className="text-foreground-400 font-normal">（限500字）</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    value={contactForm.message}
                    onChange={(e) => setContactForm((prev) => ({ ...prev, message: e.target.value }))}
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-xl text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                    placeholder="请输入您想说的话..."
                  />
                  <p className="text-xs text-foreground-400 mt-1 text-right">{contactForm.message.length}/500</p>
                </div>
                {formError && (
                  <p className="text-sm text-primary-600 bg-primary-50 rounded-lg px-4 py-2">{formError}</p>
                )}
                <button
                  type="submit"
                  className="w-full px-6 py-3 bg-primary-500 text-background-50 rounded-xl text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  发送留言
                </button>
              </form>
            )}
          </div>
        </section>

        <section className="mb-14">
          <h2 className="font-heading text-2xl text-foreground-950 mb-6 text-center">用户口碑</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {testimonials.map((t, idx) => (
              <div
                key={idx}
                className="bg-background-50 border border-background-200/70 rounded-xl p-5"
              >
                <div className="flex items-center gap-0.5 mb-3">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-3">&ldquo;{t.content}&rdquo;</p>
                <p className="text-xs text-foreground-400 font-medium">—— {t.name}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-background-100/50 rounded-2xl p-6 md:p-8 text-center">
          <h2 className="font-heading text-2xl text-foreground-950 mb-4">加入我们</h2>
          <p className="text-sm text-foreground-600 max-w-2xl mx-auto mb-6">
            51动漫网始终欢迎热爱动漫的你加入我们的大家庭。无论你是想追最新番剧，还是寻找经典老番，51动漫网站和51动漫官网都会是你最好的选择。
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <a href="/" className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              返回首页
            </a>
            <a href="/category/riman" className="px-6 py-2.5 bg-background-50 border border-background-200/70 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer">
              浏览日漫
            </a>
            <a href="/category/guoman" className="px-6 py-2.5 bg-background-50 border border-background-200/70 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer">
              浏览国漫
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}